/**
 * XHR-based multipart upload with real byte progress.
 *
 * The generated http-client is fetch-based and fetch cannot report upload
 * progress (upload streams via ReadableStream measure the wrong moment and are
 * not available cross-browser). XMLHttpRequest.upload.onprogress remains the
 * standard way, so the document upload goes through this small helper instead
 * of apiClient.uploadDocument. It mirrors the http-client contract:
 *
 * - cookie auth (withCredentials) and the X-CSRF-Token header
 * - proactive token refresh, same as the fetch interceptor in apiConfig
 * - rejects with `{ status, error }` where `error` is the parsed JSON body, so
 *   callers can keep reading `error.error.detail` (409 duplicate signal etc.)
 * - AbortSignal support for cancelling an in-flight upload
 *
 * Fields with `undefined` or `null` values are dropped before the FormData is
 * built. The generated client stringifies them to the literal "undefined",
 * which the backend then stores verbatim (category, legal_basis) or logs as a
 * warning per upload (purpose).
 */

import { getApiBaseUrl } from '../config/apiConfig';
import AuthService from '../services/AuthService';
import { CSRF_HEADER_NAME, getCsrfToken } from '../utils/auth/csrfManager';
import { log } from '../utils/errors/logger';

export interface UploadWithProgressOptions {
  /** Called with 0..100 as bytes leave the browser. */
  onProgress?: (percent: number) => void;
  /** Aborting the signal cancels the XHR and rejects with status 0. */
  signal?: AbortSignal;
}

export interface UploadRequestError {
  status: number;
  error: unknown;
}

export function buildUploadFormData(fields: Record<string, unknown>): FormData {
  const formData = new FormData();
  for (const [key, value] of Object.entries(fields)) {
    if (value === undefined || value === null) {
      continue;
    }
    if (value instanceof Blob) {
      formData.append(key, value);
    } else if (typeof value === 'object') {
      formData.append(key, JSON.stringify(value));
    } else {
      formData.append(key, String(value));
    }
  }
  return formData;
}

function parseResponse(xhr: XMLHttpRequest): unknown {
  const text = xhr.responseText;
  if (!text) {
    return null;
  }
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return { detail: text };
  }
}

export function uploadWithProgress<T>(
  path: string,
  fields: Record<string, unknown>,
  opts: UploadWithProgressOptions = {}
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const start = () => {
      if (opts.signal?.aborted) {
        reject({ status: 0, error: { detail: 'aborted' } } satisfies UploadRequestError);
        return;
      }

      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${getApiBaseUrl()}${path}`);
      xhr.withCredentials = true;

      const csrf = getCsrfToken();
      if (csrf) {
        xhr.setRequestHeader(CSRF_HEADER_NAME, csrf);
      }

      xhr.upload.onprogress = (event: ProgressEvent) => {
        if (event.lengthComputable && opts.onProgress) {
          opts.onProgress(Math.round((event.loaded / event.total) * 100));
        }
      };

      xhr.onload = () => {
        const body = parseResponse(xhr);
        if (xhr.status >= 200 && xhr.status < 400) {
          resolve(body as T);
        } else {
          reject({
            status: xhr.status,
            error: body ?? { detail: xhr.statusText },
          } satisfies UploadRequestError);
        }
      };
      xhr.onerror = () => {
        reject({ status: 0, error: { detail: 'network' } } satisfies UploadRequestError);
      };
      xhr.onabort = () => {
        reject({ status: 0, error: { detail: 'aborted' } } satisfies UploadRequestError);
      };

      const onAbort = () => xhr.abort();
      opts.signal?.addEventListener('abort', onAbort, { once: true });
      xhr.onloadend = () => opts.signal?.removeEventListener('abort', onAbort);

      xhr.send(buildUploadFormData(fields));
    };

    // Same proactive refresh as the fetch interceptor: a 10 MB upload over a
    // slow link must not expire the session mid-request.
    if (AuthService.shouldRefreshToken()) {
      AuthService.refreshToken()
        .catch((error: unknown) => {
          log.warn('Proactive token refresh before upload failed', error);
        })
        .finally(start);
    } else {
      start();
    }
  });
}
